import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { ShowHide } from "components/layout";
import { Tooltip } from "components/tooltip";
import { isCappedEndedPeriod } from "./kpiPeriodShading";
import { describePeriodDates } from "./kpiPeriodWindow";
import { periodStates, periodStateLabel, periodStateName, periodStateMeaning } from "./erPeriodStates";
import "./EarnedRecognition.css";

const tooltipSeparator = " · ";

// A period column header for the KPI grids: what the column is, the ISO year where it changes, the ISO
// period number, its week range, and the small-operator marker, wrapped in one tooltip giving the
// period and its calendar dates, then what the state means and, for a capped period, the small-operator
// explanation.
//
// The year slot is laid out on every header and the state and small-operator slots on every header
// whenever any displayed column fills them, hidden where empty, so every header keeps the same height.
//
// ListingTable.formatHeaders reads element.key off the header element it is given, so callers must
// put the key on this component rather than on anything inside it.
//
// The two KPI dashboards describe the small-operator provision against different measures, so the
// tooltip text is supplied by the caller.
export function ErPeriodHeader(
    {
        period,
        displayState = periodStates.notCounted,
        showYear = false,
        showStateSlot = false,
        showCappedSlot = false,
        cappedMarkerTooltip = ""
    }
) {
    const isCapped = isCappedEndedPeriod(period);
    const stateLabel = periodStateLabel(displayState);
    const dates = describePeriodDates(period.isoYear, period.isoPeriod);

    const tooltipContent = (
        <div className="er-kpi-period-tooltip">
            <div className="er-kpi-period-tooltip-line">{`${dates.periodText}, ${period.isoYear}`}</div>
            <div className="er-kpi-period-tooltip-line">{`${dates.datesText}${tooltipSeparator}${dates.weeksText}`}</div>
            <div className="er-kpi-period-tooltip-state">
                <div className="er-kpi-period-tooltip-line er-kpi-period-tooltip-state-name">{periodStateName(displayState)}</div>
                <div className="er-kpi-period-tooltip-line">{periodStateMeaning(displayState)}</div>
                <ShowHide
                    evaluator={isCapped}
                    show={(<div className="er-kpi-period-tooltip-line er-kpi-period-tooltip-capped">{cappedMarkerTooltip}</div>)}
                />
            </div>
        </div>
    );

    return (
        <Tooltip content={tooltipContent}>
            <div className="er-kpi-period-header">
                <ShowHide
                    evaluator={showStateSlot}
                    show={(
                        <div className={classNames("er-kpi-period-state", { "er-kpi-period-slot-hidden": stateLabel == null })}>
                            {stateLabel ?? ""}
                        </div>
                    )}
                />
                <div className={classNames("er-kpi-period-year", { "er-kpi-period-slot-hidden": !showYear })}>
                    {showYear ? period.isoYear : ""}
                </div>
                <div className="er-kpi-period-number">{period.isoPeriod}</div>
                <div className="er-kpi-period-weeks">{`${period.isoWeekStart}–${period.isoWeekEnd}`}</div>
                <ShowHide
                    evaluator={showCappedSlot}
                    show={(
                        <span className={classNames("er-kpi-period-capped", { "er-kpi-period-capped-hidden": !isCapped })}>
                            S
                        </span>
                    )}
                />
            </div>
        </Tooltip>
    );
}

ErPeriodHeader.propTypes = {
    period: PropTypes.object.isRequired,
    displayState: PropTypes.oneOf(Object.values(periodStates)),
    showYear: PropTypes.bool,
    showStateSlot: PropTypes.bool,
    showCappedSlot: PropTypes.bool,
    cappedMarkerTooltip: PropTypes.string
};
